import { countingActiveRequests } from './entry-setup/count-active-requests';
import { Sentry } from './entry-setup/sentry';

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  // eslint-disable-next-line no-console
  if (!meta) console.error('unable to find csrf token meta tag on page');
  return meta?.getAttribute('content');
}

function getUrlLocale() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('locale');
  } catch(e) {
    return null;
  }
}

function getWafToken() {
  return window.AwsWafIntegration?.getToken();
}

// Uses the WAF SDK's fetch wrapper when it's loaded, so a Challenge response is solved and
// retried by the SDK instead of falling through to api.js's own response handling below.
function wafFetch(url, options) {
  if (window.AwsWafIntegration?.fetch) {
    return window.AwsWafIntegration.fetch(url, options);
  }
  return fetch(url, options);
}

// Thrown when a response carries the x-amzn-waf-action header, meaning the WAF interrupted the
// request (e.g. a CAPTCHA it can't render on an XHR path, or a Challenge the SDK couldn't solve)
// rather than the server actually handling it. `status` mirrors the underlying response so
// existing `err.status` checks keep working.
export class WafInterruptionError extends Error {
  constructor(response, wafAction) {
    super(`WAF interrupted the request with action: ${wafAction}`);
    this.name = 'WafInterruptionError';
    this.status = response.status;
    this.wafAction = wafAction;
  }
}

// The MFA gate refuses a request that needs a verification code, naming the screen that
// collects one. Without this the 403 would surface as a generic failure with no way forward,
// since callers collapse every rejection into a single error flag. The response is cloned so
// that reading it here leaves the body intact for the caller.
async function mfaChallengeUrl(resp) {
  if (!(resp.headers.get('Content-Type') || '').includes('application/json')) {
    return null;
  }

  try {
    const body = await resp.clone().json();
    return body?.mfa_required ? body.challenge_url : null;
  } catch (e) {
    return null;
  }
}

// A persona session that ended under an open tab. The page still shows the persona's banner
// and composer, so the server refuses the write rather than silently performing it as the
// signed-in human, and names the page that explains what happened. Same shape as the MFA
// refusal above, on 409 rather than 403.
async function personaEndedUrl(resp) {
  if (!(resp.headers.get('Content-Type') || '').includes('application/json')) {
    return null;
  }

  try {
    const body = await resp.clone().json();
    return body?.persona_ended ? body.acknowledge_url : null;
  } catch (e) {
    return null;
  }
}

/**
 * A more usable fetch, smaller than axios. It boasts:
 *  - automatically pulls the csrf token from <head>
 *  - retries if the error indicates a csrf failure
 *  - serializes data and passes json headers.
 *
 * If you need, we can extend it to also work with FormData,
 * but we should see if we can just submit the form, since that
 * works by default
 */
export default async function request({ url, method, data, query, retries = 2, csrfToken = getCsrfToken(), customHeaders = null }) {
  const locale = getUrlLocale();
  const mergedQuery = { ...query };

  if (locale) {
    mergedQuery.locale = locale;
  }

  // Store the original URL for retries (before adding query params)
  const originalUrl = url;

  if (Object.keys(mergedQuery).length > 0) {
    // eslint-disable-next-line no-param-reassign
    url += `?${new URLSearchParams(mergedQuery)}`;
  }
  const headers = {
    'accept': 'application/json',
    'x-csrf-token': csrfToken,
    ...customHeaders,
  };
  const wafToken = await getWafToken();
  if (wafToken) {
    headers['x-aws-waf-token'] = wafToken;
  }
  let body;
  if (data instanceof FormData) {
    body = data;
  } else if (data && method && !['GET', 'HEAD'].includes(method.toUpperCase())) {
    body = JSON.stringify(data);
    headers['content-type'] = 'application/json';
  }
  try {
    const resp = await countingActiveRequests(() => wafFetch(url, {
      method,
      body,
      credentials: 'same-origin',
      headers,
    }));

    const wafAction = resp.headers.get('x-amzn-waf-action');
    if (wafAction) {
      const wafError = new WafInterruptionError(resp, wafAction);
      Sentry.captureException(wafError);
      return Promise.reject(wafError);
    }

    if (resp.status === 422 && retries > 0) {
      // Only retry if server provided a new CSRF token (indicates CSRF failure)
      // Otherwise it's a validation error and retrying won't help
      const newCsrfToken = resp.headers.get('x-csrf-token');
      if (newCsrfToken && newCsrfToken !== csrfToken) {
        return request({
          url: originalUrl,
          method,
          data,
          query,
          retries: retries - 1,
          csrfToken: newCsrfToken,
          customHeaders,
        });
      }
    }

    if (resp.status === 403) {
      const challengeUrl = await mfaChallengeUrl(resp);
      if (challengeUrl) {
        window.location.assign(challengeUrl);
      }
    }

    if (resp.status === 409) {
      const endedUrl = await personaEndedUrl(resp);
      if (endedUrl) {
        window.location.assign(endedUrl);
      }
    }

    if (resp.status >= 400) {
      return Promise.reject(resp);
    }

    let jData = null;
    if (resp.status !== 204) {
      const respContentType = resp.headers.get('Content-Type');

      if (respContentType.includes('application/json')) {
        jData = await resp.json();
      } else if (respContentType.includes('text/html')) {
        jData = await resp.text();
      } else {
        return Promise.reject(resp);
      }
    }

    return {
      data: jData,
      status: resp.status,
      statusText: resp.statusText,
      headers: resp.headers,
    };
  } catch (err) {
    // Network error - retry if we have retries left
    if (retries > 0) {
      return request({
        url: originalUrl,
        method,
        data,
        query,
        retries: retries - 1,
        csrfToken,
        customHeaders,
      });
    }
    throw err;
  }
}
